import { Simplify } from "@jog/types"
import { asText } from "@prismicio/helpers"
import {
    BooleanField,
    ColorField,
    GroupField,
    ImageField,
    KeyTextField,
    NumberField,
    RichTextField,
    TitleField,
} from "@prismicio/types"
import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useState } from "react"

export interface PrismicPopup {
    render_trsusted_shops: BooleanField
    activate_popup_campaign: BooleanField
    campaignpopup_blacklist: KeyTextField
    campaignpopup_title: TitleField
    campaignpopup_description: RichTextField
    campaignpopup_options_text: RichTextField
    campaignpopup_button_text: TitleField
    campaignpopup_button_bgcolor: ColorField
    campaignpopup_button_color: ColorField
    campaignpopup_cancel_text: KeyTextField
    close_icon_bgcolor: ColorField
    close_icon_color: ColorField
    campaignpopup_activate_filter_options: BooleanField
    campaignpopup_options: GroupField<
        Simplify<{
            campaignpopup_option_key: KeyTextField
            campaignpopup_option_value: RichTextField
        }>
    >
    campaignpopup_image_desktop: ImageField
    campaignpopup_image_mobile: ImageField
    campaignpopup_thankyou_title: RichTextField
    campaignpopup_thankyou_body: RichTextField
    campaignpopup_delay: NumberField
}
export const transPopup = (data: PrismicPopup): PopupState["popup"] => {
    const renderTrustedShops = data.render_trsusted_shops
    const activatePopupCampaign = data.activate_popup_campaign
    const campaignBlacklist = (data.campaignpopup_blacklist || "").split(",")
    const campaignPopupTitle = asText(data.campaignpopup_title)
    const campaignPopupDescription = asText(data.campaignpopup_description)
    const campaignPopupButtonText = asText(data.campaignpopup_button_text)
    const campaignPopupOptionsText = asText(data.campaignpopup_options_text)
    const campaignPopupActivateFilterOptions = data.campaignpopup_activate_filter_options
    const campaignPopupButtonBgColor = data.campaignpopup_button_bgcolor || ""
    const campaignPopupButtonColor = data.campaignpopup_button_color || ""
    const campaignCancelText = data.campaignpopup_cancel_text || ""
    const closeIconBgColor = data.close_icon_bgcolor || ""
    const closeIconColor = data.close_icon_color || ""
    const campaignPopupOptionsList = [...(data.campaignpopup_options || [])].map((option) => ({
        key: option.campaignpopup_option_key || "",
        value: asText(option.campaignpopup_option_value) || "",
    }))
    const campaignPopupImageDesktop = data.campaignpopup_image_desktop
    const campaignPopupImageMobile = data.campaignpopup_image_mobile
    const campaignPopupThankyouTitle = asText(data.campaignpopup_thankyou_title) || ""
    const campaignPopupThankyouBody = data.campaignpopup_thankyou_body
    const campaignPopupDelay = data.campaignpopup_delay || 0
    return {
        renderTrustedShops,
        activatePopupCampaign,
        campaignBlacklist,
        campaignPopupTitle,
        campaignPopupDescription,
        campaignPopupButtonText,
        campaignPopupOptionsText,
        campaignPopupActivateFilterOptions,
        campaignPopupOptionsList,
        campaignPopupImageDesktop,
        campaignPopupImageMobile,
        campaignPopupThankyouTitle,
        campaignPopupThankyouBody,
        campaignPopupDelay,
        campaignPopupButtonBgColor,
        campaignPopupButtonColor,
        campaignCancelText,
        closeIconBgColor,
        closeIconColor,
    }
}

export interface PopupState {
    popup: {
        campaignPopupImageDesktop: ImageField | null
        campaignPopupThankyouBody: RichTextField | null
        campaignPopupThankyouTitle: string
        campaignPopupImageMobile: ImageField | null
        campaignBlacklist: string[]
        campaignPopupDelay: number
        campaignPopupButtonText: string
        campaignPopupTitle: string
        renderTrustedShops: boolean
        campaignPopupOptionsList: { value: string; key: string }[]
        activatePopupCampaign: boolean
        campaignPopupOptionsText: string
        campaignPopupActivateFilterOptions: boolean
        campaignPopupDescription: string
        campaignPopupButtonBgColor: string
        campaignPopupButtonColor: string
        campaignCancelText: string
        closeIconBgColor: string
        closeIconColor: string
    } | null

    error: boolean
}
export const PopupContext = createContext<PopupState>({
    popup: null,
    error: false,
})

export const PopupProvider = ({
    children,
    fetch,
}: PropsWithChildren<{
    fetch: () => Promise<PopupState["popup"]>
}>) => {
    const [popup, setPopup] = useState<PopupState["popup"]>(null)
    const [error, setError] = useState(false)

    const init = useCallback(async () => {
        if (popup) return
        try {
            const data = await fetch()
            setPopup(data)
            setError(false)
        } catch (e) {
            console.log(e)
            setPopup(null)
            setError(true)
        }
    }, [fetch, popup])
    useEffect(() => {
        init()
    }, [init])
    return <PopupContext.Provider value={{ popup, error }}>{children}</PopupContext.Provider>
}

export const usePopup = () => useContext(PopupContext)!
